import { useState, useEffect } from "react";
import "./App.css";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AuthCallback } from "./components/AuthCallback";
import { WelcomeScreen } from "./components/WelcomeScreen";
import { MainApp } from "./components/MainApp";
import { VerifyEmailScreen } from "./components/auth/VerifyEmailScreen";
import { ResetPasswordScreen } from "./components/auth/ResetPasswordScreen";
import { PrivacyPolicyPage } from "./components/legal/PrivacyPolicyPage";
import { TermsOfServicePage } from "./components/legal/TermsOfServicePage";
import { RefundPolicyPage } from "./components/legal/RefundPolicyPage";
import { DeletionStatusPage } from "./components/legal/DeletionStatusPage";
import { AboutPage } from "./components/about/AboutPage";

function parseHashRoute() {
  const rawHash = (window.location.hash || "").replace(/^#/, "");
  const h = rawHash.toLowerCase();
  const verifyMatch = rawHash.match(/^verify-email=([^&]+)/i);
  if (verifyMatch) return { kind: "verify-email", token: decodeURIComponent(verifyMatch[1]) };
  const resetMatch = rawHash.match(/^reset-password=([^&]+)/i);
  if (resetMatch) return { kind: "reset-password", token: decodeURIComponent(resetMatch[1]) };
  // Phase H.3 — Meta-required public data-deletion status page.
  // URL shape: /#deletion-status=<confirmation_code>
  const delMatch = rawHash.match(/^deletion-status=([^&]+)/i);
  if (delMatch) return { kind: "deletion-status", code: decodeURIComponent(delMatch[1]) };
  if (h === "privacy") return { kind: "privacy" };
  if (h === "terms") return { kind: "terms" };
  if (h === "refund") return { kind: "refund" };
  if (h === "about") return { kind: "about" };
  if (h.includes("session_id=")) {
    // Phase M.89 (Mar 2026) — Android sign-in bridge. When the OAuth
    // callback lands on our reserved `/android-oauth` path, we hand
    // the session_id off to the Android app via a custom scheme deep
    // link instead of running the normal web-cookie exchange.
    //
    // We use a PATH marker (not `?android=1` query) because
    // Emergent's redirect handling has been observed to normalize
    // query params on the redirect URL, but path segments survive
    // the round-trip. Path check runs first so a signed-in web user
    // who somehow lands on this path still gets routed to the
    // bridge, not their MainApp session.
    //
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
    // REDIRECT URLS, THIS BREAKS THE AUTH.
    if (window.location.pathname.includes("/android-oauth")) {
      return { kind: "android-oauth-bridge" };
    }
    return { kind: "oauth-callback" };
  }
  return null;
}

function AppContent() {
  const { user, loading, refreshUser } = useAuth();
  const [authCallbackComplete, setAuthCallbackComplete] = useState(false);
  const [hashRoute, setHashRoute] = useState(() => parseHashRoute());

  // Phase 30.K — Initialize `data-beta-theme` on <html> at app boot so the
  // Beta UI (grey gradient + pink/purple glow) applies to PRE-LOGIN pages
  // (About, Privacy, Terms, Refund, Welcome) too. Previously the attribute
  // was only set after login inside MainApp, leaving public pages on the
  // legacy purple/violet palette. Default to "dark"; respect persisted choice.
  useEffect(() => {
    try {
      const persisted = localStorage.getItem("aura_beta_theme") || "dark";
      document.documentElement.setAttribute("data-beta-theme", persisted);
    } catch {
      document.documentElement.setAttribute("data-beta-theme", "dark");
    }
  }, []);

  // Capture ?ref=AURA-XXX from the URL on EVERY landing (auth or not).
  // Stash in localStorage so it survives auth redirects and tab-close-reopen.
  // MainApp's useState initializer will pick it up post-login.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref) {
        localStorage.setItem("aura_pending_ref", ref);
        // Clean the URL so a refresh doesn't re-trigger
        const cleanUrl = window.location.pathname + window.location.hash;
        window.history.replaceState(null, "", cleanUrl);
      }
    } catch (e) { /* localStorage unavailable in private mode — silently skip */ }
  }, []);

  // Re-parse on hashchange (in case the email link is opened in an already-open tab)
  useEffect(() => {
    const onHash = () => setHashRoute(parseHashRoute());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Phase 21 — Quick Access. URLs like /quick or /?quick=chat open the chat
  // panel as soon as the app loads. Pairs with iOS Shortcut / Android home
  // screen shortcuts so users can map their Action Button / Back Tap to Aura.
  useEffect(() => {
    try {
      const isQuickPath = window.location.pathname.startsWith("/quick");
      const params = new URLSearchParams(window.location.search);
      const isQuickParam = params.get("quick") === "chat" || params.get("quick") === "1";
      if (isQuickPath || isQuickParam) {
        sessionStorage.setItem("aura_quick_launch", "1");
        // Clean the URL so refresh doesn't re-trigger
        const cleanUrl = "/" + (window.location.hash || "");
        window.history.replaceState(null, "", cleanUrl);
      }
    } catch (e) { /* private mode: ignore */ }
  }, []);

  // Verify Email — works whether signed in or not
  if (hashRoute?.kind === "verify-email") {
    return (
      <VerifyEmailScreen
        token={hashRoute.token}
        onDone={async () => {
          setHashRoute(null);
          // Refresh in case user is signed in
          try { await refreshUser?.(); } catch (_) {}
        }}
      />
    );
  }

  // Reset Password — works whether signed in or not
  if (hashRoute?.kind === "reset-password") {
    return (
      <ResetPasswordScreen
        token={hashRoute.token}
        onDone={() => setHashRoute(null)}
      />
    );
  }

  // OAuth callback
  if (hashRoute?.kind === "oauth-callback" && !authCallbackComplete) {
    return <AuthCallback onComplete={() => { setAuthCallbackComplete(true); setHashRoute(null); }} />;
  }

  // Phase M.89 (Mar 2026) — Android OAuth bridge. Redirects the
  // session_id back into the Android app via a custom scheme deep
  // link. Rendered synchronously during first paint (like AuthCallback)
  // so the redirect fires before AuthProvider tries to check /auth/me
  // and set a web session cookie for the wrong device.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
  // URLS, THIS BREAKS THE AUTH.
  if (hashRoute?.kind === "android-oauth-bridge") {
    // Lazy import so the extra bundle doesn't ship with every page load;
    // this route is hit only from Android Custom Tabs.
    const { AndroidOAuthBridge } = require("./components/AndroidOAuthBridge");
    return <AndroidOAuthBridge />;
  }

  // Static legal/about pages — accessible whether signed in or not
  if (hashRoute?.kind === "privacy") {
    return <PrivacyPolicyPage onClose={() => { window.location.hash = ""; setHashRoute(null); }} />;
  }
  if (hashRoute?.kind === "terms") {
    return <TermsOfServicePage onClose={() => { window.location.hash = ""; setHashRoute(null); }} />;
  }
  if (hashRoute?.kind === "refund") {
    return <RefundPolicyPage onClose={() => { window.location.hash = ""; setHashRoute(null); }} />;
  }
  if (hashRoute?.kind === "deletion-status") {
    return <DeletionStatusPage
      confirmationCode={hashRoute.code}
      onClose={() => { window.location.hash = ""; setHashRoute(null); }}
    />;
  }
  if (hashRoute?.kind === "about") {
    return (
      <AboutPage
        onClose={() => { window.location.hash = ""; setHashRoute(null); }}
        onGetStarted={() => { window.location.hash = ""; setHashRoute(null); }}
      />
    );
  }

  if (loading) {
    return (
      <div className="app-container">
        <div className="wallpaper-placeholder">
          <h1>✨</h1>
          <p>Connecting to your Aura...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Phase 30.L — Founder flipped the default. The first landing surface is now
    // the redesigned WelcomeScreen (giant "AURA" wordmark + public chat + CTAs
    // + Coming Soon marquee/icon grid). AboutPage is still reachable via #about
    // for the long-form marketing page.
    return <WelcomeScreen />;
  }

  return <MainApp />;
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
